import { AccountRequiredState } from 'types/account';
import { getLoginMobileVerifyMeta } from '@api/login';
import { isPasswordVerified } from '@api/user';
import { login } from '@store/modules/login';
import { setItemInSessionStorage } from '@utils/web-storage';
import { user } from '@store/modules/user';

const userHasCurrentPhone = (): boolean => {
	return !!user.phoneData.currentPhoneNumber;
};

const userPhoneNotVerified = (): boolean => {
	return !user.phoneData.currentPhoneNumberValidated;
};

const userIsNotRestrictedFromVisitingMobileVerifyFlow = async (): Promise<boolean> => {
	try {
		const meta = await getLoginMobileVerifyMeta();
		return !(meta.hasVisitedInSession || meta.hasExceededMaxPrompts);
	} catch {
		return false;
	}
};

const userHasCompletedSelectInCurrentSession = (): boolean => {
	return login.hasCompletedLoginMobileVerifySelect;
};

const getFunction = (requiredState: AccountRequiredState): (() => boolean | Promise<boolean>) => {
	switch (requiredState) {
		case 'userHasCurrentPhone':
			return userHasCurrentPhone;
		case 'userPhoneNotVerified':
			return userPhoneNotVerified;
		case 'userIsNotRestrictedFromVisitingMobileVerifyFlow':
			return userIsNotRestrictedFromVisitingMobileVerifyFlow;
		case 'userHasCompletedSelectInCurrentSession':
			return userHasCompletedSelectInCurrentSession;
		default:
			return userHasCurrentPhone;
	}
};

const checkRequiredStates = async (requiredStates: Array<AccountRequiredState>): Promise<boolean> => {
	for (let i = 0; i < requiredStates.length; i++) {
		if (!(await getFunction(requiredStates[i])())) {
			return false;
		}
	}
	return true;
};

export const verifyRequiredStates = async (
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	next: any,
	requiredStates: Array<AccountRequiredState>
): Promise<void> => {
	try {
		const isValidStep = await checkRequiredStates(requiredStates);
		if (isValidStep) {
			await next();
		} else {
			await next({ name: 'settings-mobile-number-view' });
		}
	} catch {
		await next({ name: 'settings-mobile-number-view' });
	}
};

export const confirmPasswordVerified = async (
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	next: any,
	redirectRoute: string,
	cancelRoute: string
): Promise<void> => {
	const passwordVerified = await isPasswordVerified();
	if (passwordVerified) {
		await next();
	} else {
		setItemInSessionStorage('verify-password-redirect', redirectRoute);
		setItemInSessionStorage('verify-password-cancel', cancelRoute);
		await next({ name: 'password-verify' });
	}
};
